import { endsWith } from 'lodash';
import { FormEvent, PropsWithChildren } from 'react';
import { FieldValues, FormProvider, Path, UnpackNestedValue, UseFormReturn } from 'react-hook-form';
import { PropsWithClassName } from 'src/shared/utils/types/PropsWithClassName';

import { Button } from '@shared/ui/components/Button';
import { useToast } from '@shared/ui/components/Toast';
import { cn } from '@shared/utils/helpers';

type FormProps<T extends FieldValues, R> = PropsWithChildren<
  PropsWithClassName<{
    form: UseFormReturn<T> & { loaded: boolean };
    action: (data: T) => Promise<R> | R;
    submitLabel?: string;
    confirmation?: {
      title?: string;
      description?: string;
    };
    onSave?: (response: R) => void;
    onSaveAndReturn?: (response: R) => void;
    onCancel?: () => void;
  }>
>;

export function Form<T extends FieldValues, R>({
  form,
  submitLabel = 'Save',
  onSave,
  onCancel,
  onSaveAndReturn,
  action,
  confirmation,
  className,
  children,
}: FormProps<T, R>) {
  const { toast } = useToast();

  function handleSubmit(event?: FormEvent): Promise<R> {
    return new Promise((resolve, reject) => {
      form.clearErrors();

      return form.handleSubmit((data: UnpackNestedValue<T>) => {
        Promise.resolve(action(data as T)).then(
          (response) => {
            toast({
              title: confirmation?.title ?? 'Saved',
              description: confirmation?.description ?? 'Your changes were saved successfully.',
            });

            resolve(response);
          },
          (error) => {
            const errorMessage = error.response?.data?.message ?? 'An unknown error occurred.';

            toast({
              title: 'Error',
              description: endsWith(errorMessage, '.') ? errorMessage : `${errorMessage}.`,
              variant: 'destructive',
            });

            if (error.response?.data?.errors) {
              for (const field in error.response.data.errors) {
                form.setError(field as Path<T>, { message: error.response.data.errors[field][0] });
              }
            }

            reject(error);
          }
        );
      })(event);
    });
  }

  function handleSave(event?: FormEvent) {
    handleSubmit(event).then(onSave);
  }

  function handleSaveAndReturn() {
    handleSubmit().then(onSaveAndReturn);
  }

  return (
    <FormProvider {...form}>
      <form className={cn('grid gap-8', className, { hidden: !form.loaded })} onSubmit={handleSave}>
        {children}
        <div className="flex items-center gap-2 justify-end">
          {onSave && <Button type="submit">{submitLabel}</Button>}
          {onSaveAndReturn && (
            <Button type="button" onClick={handleSaveAndReturn}>
              {submitLabel} and Return
            </Button>
          )}
          {onCancel && (
            <Button type="button" variant="link" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
